/**
 * JRS Analytics — EventBus → dataLayer pump
 * Version: 7.0.0 (fireAnalyticsEvent direct call surface added — 2026-05-16)
 *
 * ============================================================================
 * Revision notes (v7.0.0 — Phase 1 telemetry wrapper)
 * ============================================================================
 *
 * Adds a single new listener: the 'analytics:event' EventBus channel.
 *
 * The customizer popup shell (jrs-v2-customizer-popup.js v0.2.0) emits
 * fully-formed Doc 3 v0.2 events through this channel — common payload
 * (event_id, popup_session_id, popup_version, page context, device context,
 * timing) plus event-specific fields, all pre-built and ready for dataLayer.
 *
 * This wrapper:
 *   1. Subscribes to 'analytics:event' on EventBus
 *   2. Extracts the `event` field as the event name (per _pushEvent contract)
 *   3. Calls _pushEvent(name, restOfPayload) which:
 *        - Pushes { ecommerce: null } to clear stale ecommerce state
 *        - Pushes the event with all common + specific fields
 *   4. Wraps the push in try/catch per Doc 3 §8.3 (fail-silent on dataLayer
 *      unavailable / ad-blocker)
 *   5. Records a debug entry in _debugLog (existing pattern)
 *
 * Why _pushEvent and not a direct dataLayer.push:
 *   - Consistent with the existing 20+ legacy-channel mappings in this file
 *   - The `ecommerce: null` clear is harmless for Doc 3 events (which don't
 *     carry an ecommerce block) and a safety net if any future Doc 3 event
 *     does add ecommerce fields
 *   - Same code path for legacy and new events means one bug surface, not two
 *
 * What this wrapper does NOT do:
 *   - sendBeacon for navigation-triggered events (pagehide / popstate). The
 *     shell handles those itself in _handlePageHide(); they never reach
 *     EventBus for those triggers — see jrs-v2-customizer-popup.js v0.2.0
 *     §"Helpers" / _handlePageHide().
 *   - Payload transformation. The shell sends canonical Doc 3 v0.2 payloads;
 *     the wrapper passes them through unchanged.
 *   - Filtering. Every event the shell emits via analytics:event reaches
 *     dataLayer; no allow-list, no rate limit.
 *
 * Co-existence with the existing 20+ legacy mappings:
 *   - The new wrapper handles only the 'analytics:event' channel, which is
 *     unique to the Doc 3 v0.2 popup shell.
 *   - Legacy channels (cart:addSucceeded, customizer:stepCompleted, etc.)
 *     continue to fire their own jrs_* GA4 events. No overlap with Doc 3 v0.2
 *     event names (which are all pdp_customizer_*).
 *   - Per Doc 3 §4.11: the theme's existing jrs_add_to_cart on cart:addSucceeded
 *     remains the canonical add_to_cart fire. The popup's pdp_customizer_confirm
 *     is independent and does NOT carry an ecommerce block.
 *
 * No breaking changes to the existing API (init / destroy unchanged).
 * ============================================================================
 *
 * ============================================================================
 * Revision notes (v7.0.0 — Direct call surface — 2026-05-16)
 * ============================================================================
 *
 * Adds `JRS.Analytics.fireAnalyticsEvent(eventName, payload)` as a direct
 * call surface for step controllers and any other module that needs to
 * fire Doc 3 v0.2 events without round-tripping through the EventBus
 * `'analytics:event'` channel.
 *
 * The audit `_audit/phase-2-readiness-audit.md` (2026-05-16) flagged that
 * v7.0.0 added the EventBus listener but never exposed the direct wrapper;
 * starter step controller `jrs-v2-step-review.js` assumes the wrapper
 * exists. v7.0.0 closes that gap.
 *
 * What the wrapper does:
 *   1. Validates `eventName` is a non-empty string (warn + no-op otherwise).
 *   2. Normalises `payload` to {} when missing or non-object.
 *   3. Calls `_buildCommonPayload()` to assemble Doc 3 §3 common fields
 *      (event_id, popup_session_id, popup_version, page+device+timing).
 *   4. Merges common + caller-specific fields (caller wins on duplicates,
 *      so a controller can override e.g. ms_since_popup_open if it needs).
 *   5. Pushes via the existing `_pushEvent()` path so all events — direct
 *      and EventBus-mediated — share one pipeline.
 *   6. Records a debug entry mirroring `_handlePopupEvent`'s logging.
 *   7. Wraps the whole thing in try/catch per Doc 3 §8.3 (fail-silent on
 *      any error; never throw into the user flow).
 *
 * New internal helpers:
 *   - `_buildCommonPayload()` — Doc 3 §3 common-field builder.
 *   - `_readPageContext()` — reads from `JRS.PDPController.getContext()` if
 *     present; otherwise returns `{}`. The PDP controller isn't yet
 *     wired to provide context (Phase 2 will extend it).
 *   - `_deviceClass()` — mirrors the shell's thresholds (Doc 3 §3 / Doc 2 §3.2).
 *   - `_uuid()` — local UUID v4 with crypto / JRS.generateUUID / Math.random
 *     fallback chain (matches the shell's helper).
 *
 * The previous v7.0.0 stamp wasn't wrong about the EventBus listener — that
 * code path is genuinely complete. The audit-noted gap was the absence of
 * the direct wrapper; this revision adds it.
 *
 * No breaking changes. v7.0.0's EventBus listener continues to handle any
 * code path that emits via `analytics:event`. The direct wrapper is a pure
 * addition.
 * ============================================================================
 */

window.JRS = window.JRS || {};

(function(JRS) {
  'use strict';

  var EventBus = JRS.EventBus;
  var CartAPI = JRS.CartAPI;

  /** Module version, surfaced for debugging and embedded in debug-log writes.
      B5 — 8.0.0 marked the run-2 relaunch (1 Aug 2026); 8.1.x marks the
      run-3 base ("base", 3 Sep 2026): every order carrying
      _jrs_debug_version 8.1.x is a run-3 order (8.1.0 = 03 Sep publish, 8.1.1 = 429 + orphan fix, 8.1.2 = per-size verification fix, 8.1.3 = heal parity fix), permanently distinguishable
      from run-2 (8.0.0) and run-1 (7.0.0) in exports and analysis. */
  // v8.2.0 — derived from the theme stamp (theme.liquid sets window._bcThemeVersion before
  // any deferred script runs); the literal is only the fallback for a page without the stamp.
  var VERSION = String((typeof window !== 'undefined' && window._bcThemeVersion) || '').replace(/^\d+-/, '') || 'JRSBASE-V8.3.0-09102026-BYOB';

  var _initialized = false;
  var _unsubscribers = [];
  var _sectionObserver = null;
  var _scrollDepthMarks = {};
  var _debugLog = [];
  var _persistInterval = null;
  var _scrollThrottleTimer = null;
  var _scrollHandler = null;

  var MAX_DEBUG_LOG_ENTRIES = 50;

  function init() {
    _sweepStaleCheckoutSnapshot();
    if (_initialized) return;
    _initialized = true;

    // 2026-05-18 — read JRS.EventBus and JRS.CartAPI fresh per init() to
    // survive any load-order drift. The module-scope snapshots taken at
    // IIFE-eval time can be undefined if a dependency is loaded later
    // than Analytics; reading fresh here is belt-and-braces. The previous
    // silent `return` on missing EventBus was also masking the Closeout 14a
    // bug (init() was never called at all), so failures here now log a
    // warning instead of silently dropping the subscription. See Closeout
    // 14a fix.
    EventBus = JRS.EventBus;
    CartAPI = JRS.CartAPI;

    if (!EventBus || typeof EventBus.on !== 'function') {
      if (window.console && console.warn) {
        console.warn(
          'JRS.Analytics.init: EventBus not available — analytics:event and ' +
          '20-channel mapping subscriptions skipped. Check the script load ' +
          'order in theme.liquid (jrs-v2-event-bus.js must precede jrs-v2-analytics.js).'
        );
      }
      return;
    }

    _bindEventListeners();
    _bindPopupEventListener();   // v7.0.0 — Doc 3 v0.2 wrapper
    _initSectionTracking();
    _initScrollDepthTracking();

    _persistInterval = setInterval(function() {
      if (_debugLog.length > 0) _persistDebugLog();
    }, 30000);
  }

  function destroy() {
    for (var i = 0; i < _unsubscribers.length; i++) {
      if (typeof _unsubscribers[i] === 'function') {
        try {
          _unsubscribers[i]();
        } catch (error) {
          console.warn('Analytics: unsubscribe failed', error);
        }
      }
    }
    _unsubscribers = [];

    if (_sectionObserver) {
      try {
        _sectionObserver.disconnect();
      } catch (error) {}
      _sectionObserver = null;
    }

    if (_scrollHandler) {
      window.removeEventListener('scroll', _scrollHandler);
      _scrollHandler = null;
    }

    if (_scrollThrottleTimer) {
      clearTimeout(_scrollThrottleTimer);
      _scrollThrottleTimer = null;
    }

    if (_persistInterval) {
      clearInterval(_persistInterval);
      _persistInterval = null;
    }

    _initialized = false;
    _scrollDepthMarks = {};
    _debugLog = [];
  }

  /**
   * @param {string} eventName
   * @param {Object} params
   * @returns {void}
   */
  function _pushEvent(eventName, params) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    var eventObj = { event: eventName };

    if (params) {
      for (var key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
          eventObj[key] = params[key];
        }
      }
    }
    // v8.2.0 — every event this wrapper pushes carries the build that produced it,
    // derived from the theme stamp (never a literal — _jrs_verify_mismatch shipped
    // with a hard-coded '8.1.2' and was stale within a day).
    if (typeof eventObj.theme_build === 'undefined' || eventObj.theme_build === null) {
      eventObj.theme_build = String(window._bcThemeVersion || window.JRS_BUILD_ID || '') || null;
    }

    window.dataLayer.push(eventObj);
  }

  function _bindEventListeners() {
    var mappings = [
      { event: 'product:colourSelected', ga4: 'jrs_colour_selected', transform: function(d) {
        return { category_index: d.categoryIndex, colour: d.colour, product_handle: d.productHandle };
      }},
      { event: 'product:sizesChanged', ga4: 'jrs_sizes_changed', transform: function(d) {
        return { category_index: d.categoryIndex, total_quantity: d.totalQuantity, size_count: d.sizes ? d.sizes.length : 0 };
      }},
      { event: 'pdp:stepChanged', ga4: 'jrs_pdp_step_changed', transform: function(d) {
        return { step: d.step, max_steps: d.maxSteps };
      }},
      { event: 'bundle:categoryCompleted', ga4: 'jrs_bundle_category_completed', transform: function(d) {
        return { category_index: d.categoryIndex, category_name: d.categoryName };
      }},
      { event: 'bundle:allCategoriesCompleted', ga4: 'jrs_bundle_all_complete', transform: function(d) {
        return { total_quantity: d.totalQuantity, total_categories: d.totalCategories };
      }},
      { event: 'customizer:stepCompleted', ga4: 'jrs_customizer_step', transform: function(d) {
        return { step_name: d.stepName, position_name: d.positionName || null, logo_type: d.logoType || null };
      }},
      { event: 'customizer:logoUploaded', ga4: 'jrs_logo_uploaded', transform: function(d) {
        return { logo_id: d.logoId, position_name: d.positionName, file_size: d.fileSize };
      }},
      { event: 'customization:complete', ga4: 'jrs_customization_complete', transform: function(d) {
        return { instance_id: d.instanceId };
      }},
      { event: 'customization:cancelled', ga4: 'jrs_customization_cancelled', transform: function() { return {}; }},
      { event: 'cart:addSucceeded', ga4: 'jrs_add_to_cart', transform: function(d) {
        // item_count / instance_id unchanged. The enrichment below is additive
        // (2026-09-15, GTM step 1 of 3): see _addToCartItems.
        var out = { item_count: d.items ? d.items.length : 0, instance_id: d.instanceId };
        var enriched = _addToCartItems(d.response, d.items);
        for (var k in enriched) {
          if (Object.prototype.hasOwnProperty.call(enriched, k)) out[k] = enriched[k];
        }
        return out;
      }},
      // 2026-09-14 — "Add your logo" on plain cart lines (jrs-v2-cart-add-logo.js).
      { event: 'cart:addLogoCtaImpression', ga4: 'jrs_cart_add_logo_cta_impression', transform: function(d) {
        return _addLogoCtaParams(d);
      }},
      { event: 'cart:addLogoCtaClicked', ga4: 'jrs_cart_add_logo_cta_clicked', transform: function(d) {
        return _addLogoCtaParams(d);
      }},
      { event: 'cart:addFailed', ga4: 'jrs_add_to_cart_failed', transform: function(d) {
        return { error: d.error, instance_id: d.instanceId, http_status: d.httpStatus, shopify_description: d.shopifyDescription,
          theme_build: String(window._bcThemeVersion || window.JRS_BUILD_ID || '') || null }; // v8.2.0
      }},
      { event: 'cart:updated', ga4: 'jrs_cart_updated', transform: function(d) {
        return { item_count: d.cart ? d.cart.item_count : 0, total_price: d.cart ? d.cart.total_price : 0 };
      }},
      { event: 'cart:quantityChanged', ga4: 'jrs_cart_qty_changed', transform: function(d) {
        return { line_key: d.lineKey, old_quantity: d.oldQuantity, new_quantity: d.newQuantity, variant_id: d.variantId };
      }},
      { event: 'cart:itemRemoved', ga4: 'jrs_cart_item_removed', transform: function(d) {
        return { line_key: d.lineKey, variant_id: d.variantId, was_customized: d.wasCustomized };
      }},
      { event: 'cart:clearClicked', ga4: 'jrs_cart_clear_click', transform: function(d) {
        return { item_count: d.itemCount, total_price: d.totalPrice };
      }},
      { event: 'cart:checkoutClicked', ga4: 'jrs_cart_checkout_click', transform: function(d) {
        return { item_count: d.itemCount, total_price: d.totalPrice };
      }},
      { event: 'cart:editRequested', ga4: 'jrs_cart_edit_started', transform: function(d) {
        return { parent_key: d.parentKey, product_type: d.productType };
      }},
      { event: 'validation:started', ga4: 'jrs_validation_started', transform: function(d) {
        return { cart_item_count: d.cartItemCount };
      }},
      { event: 'validation:completed', ga4: 'jrs_validation_completed', transform: function(d) {
        return { issues_found: d.issuesFound, issues_fixed: d.issuesFixed, duration_ms: d.duration };
      }},
      { event: 'validation:issueFound', ga4: 'jrs_validation_issue', transform: function(d) {
        return { type: d.type, parent_variant_id: d.parentVariantId, details: d.details };
      }},
      { event: 'validation:checkoutBlocked', ga4: 'jrs_checkout_blocked', transform: function(d) {
        return { reason: d.reason };
      }},
      { event: 'validation:checkoutCleared', ga4: 'jrs_checkout_cleared', transform: function() { return {}; }}
    ];

    for (var i = 0; i < mappings.length; i++) {
      (function(mapping) {
        var unsub = EventBus.on(mapping.event, function(data) {
          try {
            var params = mapping.transform(data || {});
            _pushEvent(mapping.ga4, params);
            _addDebugEntry(mapping.event, 'pushed', params);
          } catch (err) {
            console.warn('Analytics: error processing ' + mapping.event, err);
          }
        });
        _unsubscribers.push(unsub);
      })(mappings[i]);
    }

    var unsubSnapshot = EventBus.on('validation:checkoutCleared', function() {
      if (!CartAPI || typeof CartAPI.getCart !== 'function' || typeof CartAPI.updateAttributes !== 'function') return;
      CartAPI.getCart().then(function(cart) {
        var snapshot = _buildCheckoutSnapshot(cart || { item_count: 0, total_price: 0, items: [] });
        // ERP-4 hygiene (2026-08-04): stamp the snapshot with the cart token so
        // a snapshot from a prior session/cart is detectable as stale (the
        // 4 Aug test cart carried a 19-item snapshot from the previous day).
        snapshot.cartToken = String((cart && cart.token) || '');
        return CartAPI.updateAttributes({
          '_jrs_checkout_snapshot': _safeStringify(snapshot)
        });
      }).catch(function() {});
    });
    _unsubscribers.push(unsubSnapshot);
  }

  // ===========================================================================
  // v7.0.0 — Doc 3 v0.2 popup analytics wrapper
  // ===========================================================================

  /**
   * Subscribe to the 'analytics:event' EventBus channel emitted by
   * jrs-v2-customizer-popup.js v0.2.0 (and any future module that needs
   * to emit pre-built Doc 3 events).
   *
   * Payload contract: full Doc 3 v0.2 event (per §3 common + §4 event-
   * specific fields). The `event` field is the event name; everything
   * else is parameters.
   *
   * Behaviour per Doc 3 §8:
   *   §8.1 — push to dataLayer (via _pushEvent for consistency)
   *   §8.3 — fail-silent on errors; never throw into the user flow
   *
   * sendBeacon is NOT handled here — the shell fires sendBeacon directly
   * from _handlePageHide() for navigation-triggered events, bypassing
   * EventBus for those (which would not survive page tear-down).
   */
  function _bindPopupEventListener() {
    if (!EventBus || typeof EventBus.on !== 'function') return;

    var unsub = EventBus.on('analytics:event', function(payload) {
      _handlePopupEvent(payload);
    });
    _unsubscribers.push(unsub);
  }

  /**
   * @param {Object} payload - Full Doc 3 v0.2 event payload
   * @returns {void}
   */
  function _handlePopupEvent(payload) {
    if (!payload || typeof payload !== 'object') {
      _addDebugEntry('analytics:event', 'dropped_invalid', { reason: 'payload not an object' });
      return;
    }

    var eventName = payload.event;
    if (typeof eventName !== 'string' || eventName.length === 0) {
      _addDebugEntry('analytics:event', 'dropped_invalid', { reason: 'missing event name' });
      return;
    }

    // Build params object by copying all fields EXCEPT `event` (which is
    // passed as the first arg to _pushEvent and would otherwise be redundant /
    // shadowed by _pushEvent's own `event: eventName` assignment).
    var params = {};
    for (var key in payload) {
      if (Object.prototype.hasOwnProperty.call(payload, key) && key !== 'event') {
        params[key] = payload[key];
      }
    }

    try {
      _pushEvent(eventName, params);
      _addDebugEntry('analytics:event', 'pushed', {
        name: eventName,
        event_id: payload.event_id || null,
        popup_session_id: payload.popup_session_id || null
      });
    } catch (err) {
      // Fail-silent per Doc 3 §8.3. The user flow must never be blocked
      // by an analytics push failure.
      console.warn('Analytics: error pushing analytics:event "' + eventName + '"', err);
      _addDebugEntry('analytics:event', 'push_failed', {
        name: eventName,
        error: err && err.message ? err.message : 'unknown'
      });
    }
  }

  // ===========================================================================
  // End v7.0.0 additions
  // ===========================================================================

  // ===========================================================================
  // v7.0.0 — fireAnalyticsEvent direct call surface
  // ===========================================================================

  /**
   * Direct call surface for Doc 3 v0.2 events. Step controllers and any
   * other module that wants to fire an event without going through the
   * EventBus `'analytics:event'` channel calls this.
   *
   * The wrapper builds Doc 3 §3 common fields from `JRS.CustomizerPopupState`
   * + page context + device context, merges in the caller's event-specific
   * payload, and pushes via `_pushEvent` (the same path EventBus events
   * use, so one bug surface).
   *
   * Errors fail silent per Doc 3 §8.3 — the user flow must never be blocked
   * by an analytics push failure.
   *
   * @param {string} eventName - Doc 3 §4 event name (e.g. 'pdp_customizer_step_view')
   * @param {Object} [payload] - Event-specific fields per Doc 3 §4.x
   * @returns {void}
   */
  function fireAnalyticsEvent(eventName, payload) {
    try {
      if (typeof eventName !== 'string' || eventName.length === 0) {
        if (window.console && console.warn) {
          console.warn(
            'JRS.Analytics.fireAnalyticsEvent: eventName must be a non-empty string',
            eventName
          );
        }
        return;
      }

      if (!payload || typeof payload !== 'object') {
        payload = {};
      }

      var common = _buildCommonPayload();

      // Merge: common fields first, then caller-specific (caller can override
      // common if it needs — rare, but supported). `event` is dropped from
      // payload here because _pushEvent assigns it from the first arg.
      var merged = {};
      var k;
      for (k in common) {
        if (Object.prototype.hasOwnProperty.call(common, k)) {
          merged[k] = common[k];
        }
      }
      for (k in payload) {
        if (Object.prototype.hasOwnProperty.call(payload, k) && k !== 'event') {
          merged[k] = payload[k];
        }
      }

      _pushEvent(eventName, merged);
      _addDebugEntry('fireAnalyticsEvent', 'pushed', {
        name: eventName,
        event_id: merged.event_id || null,
        popup_session_id: merged.popup_session_id || null
      });
    } catch (e) {
      // Fail silent per Doc 3 §8.3
      if (window.console && console.warn) {
        console.warn('JRS.Analytics.fireAnalyticsEvent:', e);
      }
    }
  }

  /**
   * Build the Doc 3 §3 common payload. Reads from JRS.CustomizerPopupState
   * (for session id + flow + openedAt), window.location (for page_path),
   * the PDP controller if available (for product context), and navigator/
   * window (for device context).
   *
   * @returns {Object}
   */
  function _buildCommonPayload() {
    var s = (window.JRS &&
             JRS.CustomizerPopupState &&
             typeof JRS.CustomizerPopupState.getState === 'function')
      ? JRS.CustomizerPopupState.getState()
      : {};
    var pageContext = _readPageContext();

    return {
      event_id: _uuid(),
      popup_session_id: s.popupSessionId || null,
      popup_version: 'v3',
      flow: s.flow || null,
      page_path: (window.location && window.location.pathname || '').slice(0, 200),
      product_id: pageContext.product_id || null,
      product_handle: pageContext.product_handle || null,
      is_bundle: !!pageContext.is_bundle,
      is_logged_in: !!pageContext.is_logged_in,
      device_class: _deviceClass(),
      viewport_width: window.innerWidth || 0,
      client_timestamp_ms: Date.now(),
      ms_since_popup_open: s.openedAt ? Date.now() - s.openedAt : 0
    };
  }

  /**
   * Read product/page context from JRS.PDPController if it exposes a
   * `getContext()` API; otherwise return an empty object. Phase 2 will
   * extend PDPController to provide canonical context.
   * @returns {Object}
   */
  function _readPageContext() {
    if (window.JRS &&
        JRS.PDPController &&
        typeof JRS.PDPController.getContext === 'function') {
      try {
        return JRS.PDPController.getContext() || {};
      } catch (e) {
        // PDPController.getContext threw — treat as no context available.
      }
    }
    return {};
  }

  /**
   * Match the shell's breakpoint thresholds (Doc 3 §3 / Doc 2 §3.2):
   *   < 768  → mobile
   *   < 1024 → tablet
   *   else   → desktop
   * @returns {'mobile'|'tablet'|'desktop'}
   */
  /**
   * 2026-09-15 — item identifiers and value for jrs_add_to_cart, read from the
   * /cart/add.js RESPONSE (the lines Shopify actually added), never from page
   * context, globals or the rendered product: that is how a stale product_id got
   * attached before. One entry per response line, in response order.
   *
   * Shopify Ajax Cart API line fields used: id (variant id), product_id,
   * product_title, variant_title, sku, final_price (unit price after line-level
   * discounts, in MINOR units, pence; falls back to price),
   * quantity, properties (bundle marker only — no property values are pushed).
   *
   * Units: price is converted pence -> pounds ONCE, here (/100). Not rounded.
   * NOTE (unverified on this store, AGENTS gotcha 8): when an added line merges
   * into an identical existing cart line, Shopify's response quantity may be the
   * merged line total rather than the quantity just added.
   *
   * No PII: no customer, email, address or free-text property values.
   *
   * 2026-09-24 — fallback. GA4 export 2026-09-15 → 09-20 (UK, consented-observed)
   * carried 264 jrs_add_to_cart events with line_count 0 while item_count (the
   * request) was 1 to 6+: the add succeeded but the parsed response had no
   * items[]. Which response shape causes it is not established. Order of use:
   *   1. response.items (the normal { items: [...] } shape);
   *   2. a single line object (the shape Shopify returns for an id + quantity add);
   *   3. the request lines (requestItems). These carry id, quantity and
   *      properties only, so item price and value are left null, never 0.
   *
   * @param {Object} response - parsed /cart/add.js JSON ({ items: [...] })
   * @param {Array} [requestItems] - the items[] body that was POSTed
   * @returns {{items: Array, value: (number|null), currency: string, line_count: number, is_bundle: boolean}}
   */
  function _addToCartItems(response, requestItems) {
    var lines = (response && Array.isArray(response.items)) ? response.items
      : (response && response.id != null && response.quantity != null) ? [response]
      : [];
    var fromRequest = false;
    if (!lines.length && Array.isArray(requestItems) && requestItems.length) {
      lines = requestItems;
      fromRequest = true;
    }
    var items = [];
    var value = 0;
    var isBundle = false;
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i] || {};
      // Unit price after line-level discounts (final_price), so a "free logo" line
      // reports £0 rather than its £8 list price. Fall back to price when the
      // response omits final_price. Order-level discounts never appear here.
      var minor = (typeof line.final_price === 'number') ? line.final_price
        : ((typeof line.price === 'number') ? line.price : null);
      var unitPrice = (minor !== null) ? minor / 100 : null; // pence -> pounds
      var qty = (typeof line.quantity === 'number') ? line.quantity : Number(line.quantity) || 0;
      items.push({
        item_id: line.id != null ? String(line.id) : (line.variant_id != null ? String(line.variant_id) : null),
        item_product_id: line.product_id != null ? String(line.product_id) : null,
        item_name: line.product_title || null,
        item_variant: line.variant_title || null,
        item_sku: line.sku || null,
        price: unitPrice,
        quantity: qty
      });
      if (unitPrice !== null) value += unitPrice * qty;
      var props = line.properties || {};
      if (props._bundle_instance_id || props._bundle) isBundle = true;
    }
    return {
      items: items,
      value: (lines.length && !fromRequest) ? value : null,
      currency: 'GBP',
      line_count: items.length,
      // Event-scoped: true when any added line carries the bundle marker
      // (_bundle_instance_id / _bundle). Overrides the always-false is_bundle
      // left in the merged dataLayer model by earlier popup pushes.
      is_bundle: isBundle
    };
  }

  function _addLogoCtaParams(d) {
    return {
      line_item_key: d.lineItemKey || null,
      product_id: d.productId || null,
      product_type: d.productType || null,
      device_class: _deviceClass(),
      theme_build: String(window._bcThemeVersion || window.JRS_BUILD_ID || '') || null,
      eligible_line_count: typeof d.eligibleLineCount === 'number' ? d.eligibleLineCount : null
    };
  }

  function _deviceClass() {
    var w = window.innerWidth || 0;
    if (w < 768) return 'mobile';
    if (w < 1024) return 'tablet';
    return 'desktop';
  }

  /**
   * UUID v4 with the same fallback chain as the shell and the state machine:
   *   JRS.generateUUID → crypto.randomUUID → Math.random fallback.
   * @returns {string}
   */
  function _uuid() {
    if (window.JRS && typeof JRS.generateUUID === 'function') {
      return JRS.generateUUID();
    }
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  // ===========================================================================
  // End v7.0.0 additions
  // ===========================================================================

  function _initSectionTracking() {
    if (typeof IntersectionObserver !== 'function') return;
    var sections = document.querySelectorAll('[data-jrs-track-section]');
    if (!sections || sections.length === 0) return;

    var observed = {};

    _sectionObserver = new IntersectionObserver(function(entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (!entry.isIntersecting) continue;

        var sectionName = entry.target.getAttribute('data-jrs-track-section');
        if (!sectionName || observed[sectionName]) continue;

        observed[sectionName] = true;
        _pushEvent('jrs_section_viewed', { section_name: sectionName });
        _addDebugEntry('section:view', 'pushed', { section_name: sectionName });
        _sectionObserver.unobserve(entry.target);
      }
    }, { threshold: 0.3 });

    for (var j = 0; j < sections.length; j++) {
      _sectionObserver.observe(sections[j]);
    }
  }

  function _initScrollDepthTracking() {
    var thresholds = [25, 50, 75, 90, 100];

    function getScrollPercent() {
      var docHeight = Math.max(
        document.body.scrollHeight, document.documentElement.scrollHeight,
        document.body.offsetHeight, document.documentElement.offsetHeight
      );
      var viewportHeight = window.innerHeight;
      var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      if (docHeight <= viewportHeight) return 100;
      return Math.round((scrollTop / (docHeight - viewportHeight)) * 100);
    }

    function onScroll() {
      var percent = getScrollPercent();
      for (var i = 0; i < thresholds.length; i++) {
        var t = thresholds[i];
        if (percent >= t && !_scrollDepthMarks[t]) {
          _scrollDepthMarks[t] = true;
          _pushEvent('jrs_scroll_depth', { depth_percent: t });
          _addDebugEntry('scroll:depth', 'pushed', { depth_percent: t });
        }
      }
    }

    _scrollHandler = function() {
      if (_scrollThrottleTimer) return;
      _scrollThrottleTimer = setTimeout(function() {
        _scrollThrottleTimer = null;
        try {
          onScroll();
        } catch (error) {}
      }, 250);
    };

    window.addEventListener('scroll', _scrollHandler, { passive: true });
  }

  /**
   * @param {string} step
   * @param {string} status
   * @param {any} details
   * @returns {void}
   */
  function _addDebugEntry(step, status, details) {
    _debugLog.push({
      step: String(step || ''),
      status: String(status || ''),
      details: details,
      time: new Date().toISOString()
    });

    if (_debugLog.length > MAX_DEBUG_LOG_ENTRIES) {
      _debugLog = _debugLog.slice(-MAX_DEBUG_LOG_ENTRIES);
    }
  }

  /**
   * @param {any} obj
   * @param {number} [maxLength]
   * @returns {string}
   */
  function _safeStringify(obj, maxLength) {
    maxLength = maxLength || 4500;
    try {
      var str = JSON.stringify(obj);
      if (str.length > maxLength) {
        var half = Math.floor(maxLength / 2) - 20;
        return str.substring(0, half) + '...[TRUNCATED]...' + str.substring(str.length - half);
      }
      return str;
    } catch (e) {
      return '{"error":"stringify_failed"}';
    }
  }

  // ERP-4 hygiene (2026-08-04): on page load, clear a checkout snapshot whose
  // cart token no longer matches the live cart (prior session/cart) — it must
  // never ride onto an order as a stale note attribute.
  function _sweepStaleCheckoutSnapshot() {
    if (!CartAPI || typeof CartAPI.getCart !== 'function' || typeof CartAPI.updateAttributes !== 'function') return;
    CartAPI.getCart().then(function(cart) {
      var raw = cart && cart.attributes && cart.attributes._jrs_checkout_snapshot;
      if (!raw) return;
      var stale = false;
      try {
        var snap = JSON.parse(raw);
        stale = !snap || String(snap.cartToken || '') !== String(cart.token || '');
      } catch (e) { stale = true; }
      if (stale) {
        CartAPI.updateAttributes({ '_jrs_checkout_snapshot': '' })['catch'](function() {});
      }
    })['catch'](function() {});
  }

  function _persistDebugLog() {
    if (!CartAPI || typeof CartAPI.updateAttributes !== 'function') return;
    CartAPI.updateAttributes({
      '_jrs_debug_version': VERSION,
      '_jrs_debug_timestamp': new Date().toISOString(),
      // ERP-4 hygiene (2026-08-04): the full debug log ships only when the
      // debug flag is on — production orders carry a one-line summary instead
      // of several KB of entries.
      '_jrs_debug_log': (window.JRS_CART_DEBUG === true)
        ? _safeStringify({ entries: _debugLog.slice(-20), totalEntries: _debugLog.length })
        : _safeStringify({ entries: [], totalEntries: _debugLog.length, gated: true })
    }).catch(function(err) {
      console.warn('Analytics: debug persist failed:', err);
    });
  }

  /**
   * @param {any} cart
   * @returns {Object}
   */
  function _buildCheckoutSnapshot(cart) {
    var safeCart = cart || { item_count: 0, total_price: 0, items: [] };
    var snapshot = {
      timestamp: new Date().toISOString(),
      itemCount: safeCart.item_count,
      totalPrice: safeCart.total_price,
      parentCount: 0,
      childCount: 0,
      plainCount: 0,
      items: []
    };

    var items = safeCart.items || [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i] || {};
      var props = item.properties || {};
      var type = 'plain';

      if (typeof props['_ParentVariant'] !== 'undefined') {
        type = 'child';
        snapshot.childCount += 1;
      } else if (props['_customization_data'] || props['_bundle_raw'] || props['_has_customization'] === 'true') {
        type = 'parent';
        snapshot.parentCount += 1;
      } else {
        snapshot.plainCount += 1;
      }

      snapshot.items.push({
        key: item.key,
        variantId: item.variant_id,
        qty: item.quantity,
        type: type,
        lineItemKey: props['_lineItemKey'] || null
      });
    }

    return snapshot;
  }

  JRS.Analytics = {
    VERSION: VERSION,
    init: init,
    destroy: destroy,
    fireAnalyticsEvent: fireAnalyticsEvent // v7.0.0
  };

  // 2026-05-18 — auto-init on script load. Closeout 14a fix: the module
  // exposes JRS.Analytics.init but historically had no caller invoking it,
  // so the EventBus subscriptions (analytics:event for shell-emitted popup
  // events, plus the 20-channel mapping including cart:addSucceeded →
  // jrs_add_to_cart) were never wired up. Workstream C QA on 2026-05-18
  // verified that JRS.CustomizerPopup.open() produced zero deltas in
  // window.dataLayer because of this. Auto-init handles both
  // readyState='loading' (defer-loaded but DOM not parsed yet) and the
  // already-loaded case.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      JRS.Analytics.init();
    });
  } else {
    JRS.Analytics.init();
  }
})(window.JRS);
